<template>
  <div class="encounter-walking-test">
    <v-form
        v-on:submit.prevent
        class="encounter-form"
    >
      <v-simple-table class="encounter-form__table no-number-ticks">
        <template v-slot:default>
          <tbody>
            <tr>
              <td>
                <!-- Note: English by specs -->
                {{ 'Was the 6 Minute Walking Test performed?' }}
              </td>
              <td>
                <v-radio-group
                    v-model="isWalkingTestPerformed"
                    :errorMessages="errorMessages.isWalkingTestPerformed"
                    @change="() => setErrorMessage('isWalkingTestPerformed')"
                    row
                    :disabled="!canEditVisitData"
                >
                  <v-radio
                      :label="$ucfirst($t('buttons.yes', {catalog: 'app_mobile', language: 'en'}))"
                      :value="true"
                  ></v-radio>
                  <v-radio
                      :label="$ucfirst($t('buttons.no', {catalog: 'app_mobile', language: 'en'}))"
                      :value="false"
                  ></v-radio>
                </v-radio-group>
              </td>
            </tr>
            <tr v-if="isWalkingTestPerformed">
              <td>
                <!-- Note: English by specs -->
                {{ 'Distance walked' }}
              </td>
              <td>
                <v-text-field
                    v-model="distanceWalked"
                    :errorMessages="errorMessages.distanceWalked"
                    @blur="() => setErrorMessage('distanceWalked')"
                    suffix="m"
                    outlined
                    dense
                    :disabled="!canEditVisitData"
                    type="number"
                    min="0"
                ></v-text-field>
              </td>
            </tr>
            <tr v-if="isWalkingTestPerformed">
              <td>
                <!-- Note: English by specs -->
                {{ 'Borg dyspnea' }}
              </td>
              <td>
                <v-select
                    :items="perceivedExertionItems"
                    v-model="perceivedExertion"
                    :errorMessages="errorMessages.perceivedExertion"
                    @blur="() => setErrorMessage('perceivedExertion')"
                    outlined
                    dense
                    :disabled="!canEditVisitData"
                ></v-select>
              </td>
            </tr>
            <tr v-if="isWalkingTestPerformed === false">
              <td class="encounter-walking-test__td">
                <!-- Note: English by specs -->
                {{ 'Reason' }}
              </td>
              <td class="encounter-walking-test__td">
                <v-radio-group
                  class="mb-0"
                  v-model="notPerformedReasonGroup"
                  :errorMessages="errorMessages.notPerformedReasonGroup"
                  @change="() => setErrorMessage('notPerformedReasonGroup')"
                  :disabled="!canEditVisitData"
                >
                  <!-- CONTRAINDICATED -->
                  <v-radio
                    label="Contraindicated"
                    value="contraindicated"
                  ></v-radio>
                  <v-radio-group
                    v-if="isWalkingTestPerformed === false && notPerformedReasonGroup === 'contraindicated'"
                    class="mt-2 pl-14"
                    v-model="notPerformedReason"
                    :errorMessages="errorMessages.notPerformedReason"
                    @change="() => setErrorMessage('notPerformedReason')"
                    :disabled="!canEditVisitData"
                  >
                    <v-radio
                      v-for="(item, index) in notPerformedReasonList"
                      :key="`${item.value}-${index}`"
                      :label="item.label"
                      :value="item.value"
                    ></v-radio>
                  </v-radio-group>
                  <!-- OTHER -->
                  <v-radio
                    :label="$ucfirst($t('units.other', {catalog: 'app_mobile', language: 'en'}))"
                    value="other"
                  ></v-radio>
                  <v-radio-group
                    v-if="isWalkingTestPerformed === false && notPerformedReasonGroup === 'other'"
                    class="mt-2 pl-14"
                    v-model="notPerformedReason"
                    :errorMessages="errorMessages.notPerformedReason"
                    @change="() => setErrorMessage('notPerformedReason')"
                    :disabled="!canEditVisitData"
                  >
                    <v-radio
                      v-for="(item, index) in notPerformedReasonList"
                      :key="`${item.value}-${index}`"
                      :label="item.label"
                      :value="item.value"
                    ></v-radio>
                  </v-radio-group>
                </v-radio-group>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-form>
  </div>
</template>

<script>
import encounterMixin from 'dashboard/mixins/encounter.mixin';
import {notPerformedReasons} from 'dashboard/store/encounter/questionnaires/walking-test';
import {UCUMUnit} from "../../../../helper/UCUMUnit";

/**
 * Component WalkingTest.vue
 *
 * @module WalkingTest
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.4] app/src/APP_DASHBOARD/templates/views/Encounter/WalkingTest.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: 'WalkingTest',
  mixins: [encounterMixin],
  data() {
    return {
      errorMessages: {
        isWalkingTestPerformed: '',
        notPerformedReasonGroup: '',
        distanceWalked: '',
        perceivedExertion: '',
        notPerformedReason: '',
      },
    };
  },
  computed: {
    getUCUMUnit: {
      get() {
        return UCUMUnit;
      }
    },
    isWalkingTestPerformed: {
      get() {
        return this.$store.getters['encounter/isWalkingTestPerformed'];
      },
      set(isTestPerformed) {
        this.$store.dispatch('encounter/setIsWalkingTestPerformed', isTestPerformed);
      },
    },
    notPerformedReasonGroup: {
      get() {
        return this.$store.getters['encounter/notPerformedReasonGroup'];
      },
      set(reasonGroup) {
        this.$store.dispatch('encounter/setNotPerformedReasonGroup', reasonGroup);
      }
    },
    notPerformedReasonOptions() {
      const notPerformedReasonGroups = ['contraindicated', 'other'];

      return notPerformedReasonGroups.reduce((accumulator, currentItem) => {
        return {
          ...accumulator,
          [currentItem]: notPerformedReasons[currentItem].map((reason) => {
            // Note: English by specs
            const labels = {
              "CONTRAINDICATED_UNSTABLE_ANGINA": "Unstable angina during previous month",
              "CONTRAINDICATED_MYOCARDIAL_INFARCT": "Myocardial infarct during previous month",
              "CONTRAINDICATED_RESTING_PULSE_TOO_HIGH": "Resting pulse rate over 120",
              "CONTRAINDICATED_BLOOD_PRESSURE_TOO_HIGH": "Systolic blood pressure over 180 or diastolic blood pressure over 100 mmHg",
              "FORGOT": "Forgot",
              "DONOTWANT": "Refused",
              "NOTIME": "No time",
              "OTHER": "Other"
            }

            const label = labels[reason];

            return ({
              label,
              value: reason,
            })
          }),
        };
      }, {});
    },
    notPerformedReasonList() {
      return this.notPerformedReasonOptions[this.notPerformedReasonGroup];
    },
    perceivedExertionItems() {
      // Note: English by specs
      return [
        {
          text: '6 - No exertion at all',
          value: 6,
        },
        {
          text: '7.5 - Extremely light',
          value: 7.5,
        },
        {
          text: '9 - Very light',
          value: 9,
        },
        {
          text: '11 - Light',
          value: 11,
        },
        {
          text: '13 - Somewhat hard',
          value: 13,
        },
        {
          text: '15 - Hard (heavy)',
          value: 15,
        },
        {
          text: '17 - Very hard',
          value: 17,
        },
        {
          text: '19 - Extremely hard',
          value: 19,
        },
        {
          text: '20 - Maximal exertion',
          value: 20,
        },
      ];
    },
    distanceWalked: {
      get() {
        return this.$store.getters['encounter/distanceWalked'];
      },
      set(distanceWalked) {
        this.$store.dispatch('encounter/setDistanceWalked', distanceWalked);
      },
    },
    notPerformedReason: {
      get() {
        return this.$store.getters['encounter/notPerformedReason'];
      },
      set(reason) {
        this.$store.dispatch('encounter/setNotPerformedReason', reason);
      },
    },
    perceivedExertion: {
      get() {
        return this.$store.getters['encounter/perceivedExertion'];
      },
      set(perceivedExertion) {
        this.$store.dispatch('encounter/setPerceivedExertion', perceivedExertion);
      },
    },
  },
  watch: {
    isWalkingTestPerformed(isWalkingTestPerformed) {
      if (isWalkingTestPerformed) {
        this.$store.dispatch('encounter/setNotPerformedReasonGroup', null);
        this.$store.dispatch('encounter/setNotPerformedReason', null);
      } else {
        this.$store.dispatch('encounter/setDistanceWalked', '');
        this.$store.dispatch('encounter/setPerceivedExertion', '');
      }
    },
    notPerformedReasonGroup(newValue, oldValue) {
      if (oldValue !== null) {
        this.$store.dispatch('encounter/setNotPerformedReason', null);
      }
    },
  },
};
</script>

<style lang="scss">
.encounter-walking-test__question {
  font-weight: 600;
  margin-right: 32px;
  margin-bottom: 30px;
}

.encounter-walking-test__td {
  border-bottom: none !important;
}
</style>